import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { DataActionConditionType } from 'cio/utils/campaigns/data-action';
import DataActionBodyAttributeRow, { APIBodyRow } from 'cio/app-models/data-action/body-attribute-row';
import { ActionType } from '../../models/action';

const DEFAULT_OPTIONS = {
  defaultType: DataActionConditionType.TriggerAttribute,
  minimumRowsCount: 1,
  stringifyEmptyRows: true,
};

export default class DataActionBodyAttributeRows {
  @tracked rows: DataActionBodyAttributeRow[] = [];

  originalRowsCount = 0;
  minimumRowsCount = 1;
  stringifyEmptyRows = true;

  @tracked defaultType = DataActionConditionType.TriggerAttribute;

  actionType: ActionType | null = null;

  constructor(
    options: {
      parsedRows?: APIBodyRow[] | null;
      defaultType?: DataActionConditionType;
      minimumRowsCount?: number;
      stringifyEmptyRows?: boolean;
      actionType?: ActionType | null;
    } = {},
  ) {
    let _options = { ...DEFAULT_OPTIONS, ...options };

    if (_options.actionType != null) {
      this.actionType = _options.actionType;
    }

    if (_options.defaultType != null) {
      this.defaultType = _options.defaultType;
    }

    if (_options.minimumRowsCount != null) {
      this.minimumRowsCount = _options.minimumRowsCount;
    }

    if (_options.stringifyEmptyRows != null) {
      this.stringifyEmptyRows = _options.stringifyEmptyRows;
    }

    if (_options.parsedRows != null) {
      this.rows = _options.parsedRows.map(
        (apiRow) => new DataActionBodyAttributeRow({ apiRow, actionType: this.actionType }),
      );
    }

    if (this.rows.length < this.minimumRowsCount) {
      for (let i = 0; i < this.minimumRowsCount; i++) {
        // we're mutating this.rows instead of overwriting it with new array to
        // not cause mutation-after-consumption errors by setting a tracked
        // value that we already accessed
        //
        // everything should still work fine, since we're doing it in
        // constructor: there's no way something already depends on the
        // previous value
        this.rows.push(this.generateRow());
      }
    }

    this.originalRowsCount = this.rows.length;
  }

  static deserialize(
    templateBodyString: string,
    options: {
      defaultType?: DataActionConditionType;
      minimumRowsCount?: number;
      stringifyEmptyRows?: boolean;
      actionType?: ActionType | null;
    } = {},
  ) {
    try {
      let parsedRows = JSON.parse(templateBodyString) as APIBodyRow[];
      return new DataActionBodyAttributeRows({
        parsedRows,
        ...options,
      });
    } catch {
      return new DataActionBodyAttributeRows(options);
    }
  }

  get nonEmptyMappedRows() {
    return this.rows.reduce<{ name: string; type: DataActionConditionType; value: string }[]>((acc, row) => {
      let trimmedName = row.name?.trim() ?? '';

      if (isEmpty(trimmedName)) {
        return acc;
      }

      return [...acc, { name: trimmedName, type: row.type, value: row.value.trim() }];
    }, []);
  }

  serialize() {
    let nonEmptyRows = this.nonEmptyMappedRows;

    if (nonEmptyRows.length === 0 && !this.stringifyEmptyRows) {
      return '';
    }

    return JSON.stringify(
      nonEmptyRows.map((row) => ({
        name: row.name,
        type: row.type,
        value: row.value.trim(),
      })),
    );
  }

  generateRow(row?: { name: string; type: DataActionConditionType; value: string }) {
    if (row == null) {
      return new DataActionBodyAttributeRow({ type: this.defaultType, actionType: this.actionType });
    }

    return new DataActionBodyAttributeRow({ apiRow: row, actionType: this.actionType });
  }

  addRow(row?: { name: string; type: DataActionConditionType; value: string }) {
    this.rows = [...this.rows, this.generateRow(row)];
  }

  removeRow(index: number) {
    this.rows = this.rows.filter((_, i) => i !== index);
  }

  get hasErrors() {
    return this.rows.some((row) => row.hasErrors);
  }

  get hasDuplicateRowNames() {
    let nonEmptyNames = this.rows.filter((row) => !isEmpty(row.name));
    return new Set(nonEmptyNames.map((row) => row.name)).size !== nonEmptyNames.length;
  }

  get hasIllegalEmptyValues() {
    return this.rows.some((row) => row.hasIllegalEmptyValue);
  }

  get isDirty() {
    return this.rows.length !== this.originalRowsCount || this.rows.some((row) => row.isDirty);
  }
}
